import React, { useState, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

const getStyles = (theme) => ({
  container: {
    backgroundColor: theme.colors.background.sidebar,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.lg,
  },
  header: {
    marginBottom: theme.spacing.lg,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: theme.colors.text.primary,
    margin: 0,
  },
  description: {
    fontSize: '14px',
    color: theme.colors.text.secondary,
    margin: 0,
    lineHeight: '1.5',
  },
  fieldGroup: {
    marginBottom: theme.spacing.lg,
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: theme.colors.text.primary,
    marginBottom: theme.spacing.sm,
  },
  required: {
    color: theme.colors.danger,
    marginLeft: '2px',
  },
  fieldDescription: {
    fontSize: '12px',
    color: theme.colors.text.muted,
    margin: 0,
    marginBottom: theme.spacing.sm,
  },
  input: {
    width: '100%',
    padding: theme.spacing.sm,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.background.main,
    color: theme.colors.text.primary,
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  inputError: {
    borderColor: theme.colors.danger,
  },
  textarea: {
    fontFamily: 'inherit',
    minHeight: '80px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: theme.colors.text.primary,
    cursor: 'pointer',
  },
  checkbox: {
    marginRight: theme.spacing.sm,
    transform: 'scale(1.1)',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: theme.colors.text.primary,
    cursor: 'pointer',
  },
  radio: {
    marginRight: theme.spacing.sm,
  },
  errorMessage: {
    fontSize: '12px',
    color: theme.colors.danger,
    marginTop: theme.spacing.xs,
  },
  submitSection: {
    marginTop: theme.spacing.xl,
    borderTop: `1px solid ${theme.colors.border}`,
    paddingTop: theme.spacing.lg,
  },
  submitButton: {
    backgroundColor: theme.colors.primary,
    color: theme.colors.text.inverse,
    border: 'none',
    borderRadius: theme.borderRadius.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  submitButtonDisabled: {
    backgroundColor: theme.colors.text.muted,
    cursor: 'not-allowed',
  },
  formError: {
    backgroundColor: theme.colors.error.background,
    color: theme.colors.error.text,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    border: `1px solid ${theme.colors.error.border}`,
    fontSize: '14px',
    marginBottom: theme.spacing.md,
  },
});

/**
 * Structured Form Component
 * Renders dynamic forms that can submit responses back to the agent
 */
const StructuredForm = ({
  fields = [],
  title,
  description,
  submitLabel = 'Submit',
  onFormSubmit,
  formId,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [formData, setFormData] = useState(() => {
    const initial = {};
    fields.forEach((field) => {
      initial[field.name] = field.defaultValue || '';
    });
    return initial;
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const validateField = (field, value) => {
    if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return `${field.label} is required`;
    }

    if (field.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address';
      }
    }

    if (field.type === 'number' && value !== '') {
      const num = Number(value);
      if (isNaN(num)) {
        return 'Please enter a valid number';
      }
      if (field.min !== undefined && num < field.min) {
        return `Value must be at least ${field.min}`;
      }
      if (field.max !== undefined && num > field.max) {
        return `Value must be at most ${field.max}`;
      }
    }

    if (field.minLength && value.length < field.minLength) {
      return `Must be at least ${field.minLength} characters`;
    }

    if (field.maxLength && value.length > field.maxLength) {
      return `Must be at most ${field.maxLength} characters`;
    }

    if (field.pattern && !new RegExp(field.pattern).test(value)) {
      return field.patternMessage || 'Invalid format';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    fields.forEach((field) => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!onFormSubmit) {
      console.warn('No form submit handler provided');
      return;
    }

    setSubmitting(true);
    try {
      await onFormSubmit(formId, formData);
    } catch (err) {
      console.error('Form submission error:', err);
      setErrors({ _form: err.message || 'Failed to submit form' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];

    const fieldProps = {
      id: field.name,
      name: field.name,
      value,
      onChange: (e) => handleInputChange(field.name, e.target.value),
      style: {
        ...styles.input,
        ...(error && styles.inputError),
      },
      placeholder: field.placeholder,
      disabled: submitting,
    };

    let inputElement;

    switch (field.type) {
      case 'textarea':
        inputElement = (
          <textarea
            {...fieldProps}
            rows={field.rows || 3}
            style={{
              ...fieldProps.style,
              ...styles.textarea,
              resize: 'vertical',
            }}
          />
        );
        break;

      case 'select':
        inputElement = (
          <select {...fieldProps} style={fieldProps.style}>
            <option value="">Select an option...</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        break;

      case 'checkbox':
        inputElement = (
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleInputChange(field.name, e.target.checked)}
              style={styles.checkbox}
              disabled={submitting}
            />
            {field.checkboxLabel || field.label}
          </label>
        );
        break;

      case 'radio':
        inputElement = (
          <div style={styles.radioGroup}>
            {field.options?.map((option) => (
              <label key={option.value} style={styles.radioLabel}>
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  style={styles.radio}
                  disabled={submitting}
                />
                {option.label}
              </label>
            ))}
          </div>
        );
        break;

      default:
        inputElement = <input {...fieldProps} type={field.type || 'text'} />;
    }

    return (
      <div key={field.name} style={styles.fieldGroup}>
        {field.type !== 'checkbox' && (
          <label htmlFor={field.name} style={styles.label}>
            {field.label}
            {field.required && <span style={styles.required}>*</span>}
          </label>
        )}
        {field.description && <p style={styles.fieldDescription}>{field.description}</p>}
        {inputElement}
        {error && <div style={styles.errorMessage}>{error}</div>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ ...styles.container, ...style }} className={className}>
      {(title || description) && (
        <div style={styles.header}>
          {title && (
            <h3
              style={{
                ...styles.title,
                marginBottom: description ? theme.spacing.sm : 0,
              }}
            >
              {title}
            </h3>
          )}
          {description && <p style={styles.description}>{description}</p>}
        </div>
      )}

      {errors._form && <div style={styles.formError}>{errors._form}</div>}

      {fields.map(renderField)}

      <div style={styles.submitSection}>
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...styles.submitButton,
            ...(submitting && styles.submitButtonDisabled),
          }}
        >
          {submitting ? 'Submitting...' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default StructuredForm;
